import { useCallback, useEffect, useState } from 'react';
import { useI18n } from '../i18n';
import { ConfirmDialog } from './ConfirmDialog';
import { InputDialog } from './InputDialog';
import type { CheckboxChoiceOption } from '../utils/feedback';

type ConfirmState = {
  title: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'primary' | 'danger';
  resolve: (confirmed: boolean) => void;
} | null;

type TextInputState = {
  title: string;
  description?: string;
  initialValue?: string;
  placeholder?: string;
  multiline?: boolean;
  rows?: number;
  confirmText?: string;
  cancelText?: string;
  variant?: 'primary' | 'danger';
  resolve: (value: string | null) => void;
} | null;

type CheckboxChoiceState = {
  title: string;
  description?: string;
  options: CheckboxChoiceOption[];
  allowEmptySelection?: boolean;
  confirmText?: string;
  cancelText?: string;
  variant?: 'primary' | 'danger';
  resolve: (value: Record<string, boolean> | null) => void;
} | null;

export function FeedbackProvider({ children }: { children: React.ReactNode }) {
  const { t } = useI18n();
  const [confirmState, setConfirmState] = useState<ConfirmState>(null);
  const [textInputState, setTextInputState] = useState<TextInputState>(null);
  const [checkboxChoiceState, setCheckboxChoiceState] = useState<CheckboxChoiceState>(null);
  const [checkboxValues, setCheckboxValues] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<ConfirmState>).detail;
      if (!detail?.title || typeof detail.resolve !== 'function') return;
      setConfirmState(detail);
    };

    window.addEventListener('app:confirm', handler);
    return () => window.removeEventListener('app:confirm', handler);
  }, []);

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<TextInputState>).detail;
      if (!detail?.title || typeof detail.resolve !== 'function') return;
      setTextInputState(detail);
    };

    window.addEventListener('app:text-input', handler);
    return () => window.removeEventListener('app:text-input', handler);
  }, []);

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<CheckboxChoiceState>).detail;
      if (!detail?.title || typeof detail.resolve !== 'function' || !Array.isArray(detail.options)) return;
      setCheckboxValues(Object.fromEntries(detail.options.map((option) => [option.id, Boolean(option.defaultChecked)])));
      setCheckboxChoiceState(detail);
    };

    window.addEventListener('app:checkbox-choice', handler);
    return () => window.removeEventListener('app:checkbox-choice', handler);
  }, []);

  const closeConfirm = useCallback((confirmed: boolean) => {
    setConfirmState((current) => {
      if (!current) return current;
      current.resolve(confirmed);
      return null;
    });
  }, []);

  const closeTextInput = useCallback((value: string | null) => {
    setTextInputState((current) => {
      if (!current) return current;
      current.resolve(value);
      return null;
    });
  }, []);

  const closeCheckboxChoice = useCallback((value: Record<string, boolean> | null) => {
    setCheckboxChoiceState((current) => {
      if (!current) return current;
      current.resolve(value);
      return null;
    });
  }, []);

  const selectedCheckboxCount = checkboxChoiceState?.options
    .filter((option) => checkboxValues[option.id])
    .length || 0;

  return (
    <>
      {children}
      <ConfirmDialog
        open={Boolean(confirmState)}
        title={confirmState?.title || ''}
        description={confirmState?.description}
        confirmText={confirmState?.confirmText || t('common.continue')}
        cancelText={confirmState?.cancelText || t('common.cancel')}
        variant={confirmState?.variant || 'primary'}
        onConfirm={() => closeConfirm(true)}
        onCancel={() => closeConfirm(false)}
      />
      <InputDialog
        open={Boolean(textInputState)}
        title={textInputState?.title || ''}
        description={textInputState?.description}
        initialValue={textInputState?.initialValue}
        placeholder={textInputState?.placeholder}
        multiline={textInputState?.multiline}
        rows={textInputState?.rows}
        confirmText={textInputState?.confirmText || t('common.save')}
        cancelText={textInputState?.cancelText || t('common.cancel')}
        variant={textInputState?.variant || 'primary'}
        onConfirm={(value) => closeTextInput(value)}
        onCancel={() => closeTextInput(null)}
      />
      <ConfirmDialog
        open={Boolean(checkboxChoiceState)}
        title={checkboxChoiceState?.title || ''}
        description={checkboxChoiceState?.description}
        confirmText={checkboxChoiceState?.confirmText || t('common.continue')}
        cancelText={checkboxChoiceState?.cancelText || t('common.cancel')}
        variant={checkboxChoiceState?.variant || 'primary'}
        confirmDisabled={!checkboxChoiceState?.allowEmptySelection && selectedCheckboxCount === 0}
        onConfirm={() => closeCheckboxChoice(checkboxValues)}
        onCancel={() => closeCheckboxChoice(null)}
      >
        <div className="space-y-3">
          {(checkboxChoiceState?.options || []).map((option) => (
            <label
              key={option.id}
              className={`flex gap-3 rounded-lg border px-3 py-2.5 text-sm ${
                option.disabled
                  ? 'border-gray-100 bg-gray-50 text-gray-400'
                  : 'border-gray-200 bg-white text-gray-800'
              }`}
            >
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                checked={Boolean(checkboxValues[option.id])}
                disabled={option.disabled}
                onChange={(event) => {
                  const checked = event.target.checked;
                  setCheckboxValues((prev) => ({ ...prev, [option.id]: checked }));
                }}
              />
              <span className="min-w-0">
                <span className="block font-medium">{option.label}</span>
                {option.description ? (
                  <span className="mt-0.5 block text-xs text-gray-500">{option.description}</span>
                ) : null}
              </span>
            </label>
          ))}
        </div>
      </ConfirmDialog>
    </>
  );
}
